import {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  isHTMLElement,
  LexicalEditor,
  LexicalUpdateJSON,
  NodeKey,
  Spread,
  $applyNodeReplacement,
} from 'lexical';
import { SerializedTableCellNode, TableCellNode } from '@lexical/table';

export type SerializedExtendedTableCellNode = Spread<
  { borderStyle?: string },
  SerializedTableCellNode
>;

const SIDES = ['top', 'right', 'bottom', 'left'] as const;
type Side = (typeof SIDES)[number];

// 한 변의 border 를 'width style color' 로 환원. 안 보이는 변(폭 0/none/hidden)은 'none'.
function readSideBorder(style: CSSStyleDeclaration, side: Side): string {
  const width = style.getPropertyValue(`border-${side}-width`);
  const lineStyle = style.getPropertyValue(`border-${side}-style`);
  const color = style.getPropertyValue(`border-${side}-color`);
  const visible =
    parseFloat(width || '0') > 0 &&
    lineStyle !== 'none' &&
    lineStyle !== 'hidden';
  return visible
    ? `${width} ${lineStyle || 'solid'} ${color || '#000000'}`
    : 'none';
}

// 붙여넣은 셀의 4방향 border 를 인라인 CSS 로 환원한다.
// @lexical/table 기본 셀은 border 를 보존하지 않고, 테마 CSS 가 모든 셀에 border 를 강제로 긋는다.
// → 셀별 border 를 인라인으로 명시해 덮어쓰고, 선언 안 된 변은 none 으로 채워 원본에 없던 테두리가 생기지 않게 한다.
function readCellBorder(el: HTMLElement): string {
  const vals = SIDES.map((side) => readSideBorder(el.style, side));
  if (vals.every((v) => v === vals[0])) return `border: ${vals[0]}`;
  return SIDES.map((side, i) => `border-${side}: ${vals[i]}`).join('; ');
}

/**
 * TableCellNode 를 얇게 확장해 셀 border 를 보존한다(붙여넣기 스타일 유지).
 * 커스텀 nodes/TableNode.tsx(tablesheet, 셀 평문화)와 무관 — @lexical/table 셀의 리치 콘텐츠 로직은 그대로 상속.
 * importDOM(붙여넣기/HTML 로드) 경유 셀만 border 를 명시하고, 툴바로 삽입한 셀은 __borderStyle 이 없어 테마 기본값 유지.
 */
export class ExtendedTableCellNode extends TableCellNode {
  __borderStyle?: string;

  static getType(): string {
    return 'extended-table-cell';
  }

  static clone(node: ExtendedTableCellNode): ExtendedTableCellNode {
    return new ExtendedTableCellNode(
      node.__headerState,
      node.__colSpan,
      node.__width,
      node.__key,
    );
  }

  afterCloneFrom(node: this): void {
    super.afterCloneFrom(node);
    this.__borderStyle = node.__borderStyle;
  }

  static importDOM(): DOMConversionMap | null {
    const base = TableCellNode.importDOM();
    if (!base) return null;
    const patch = (tag: 'td' | 'th') => (domNode: HTMLElement) => {
      const original = base[tag]?.(domNode);
      if (!original) return null;
      return {
        ...original,
        priority: 1 as const,
        conversion: (node: HTMLElement): DOMConversionOutput | null => {
          const output = original.conversion(node);
          if (output?.node instanceof ExtendedTableCellNode) {
            output.node.__borderStyle = readCellBorder(node);
          }
          return output;
        },
      };
    };
    return { td: patch('td'), th: patch('th') };
  }

  static importJSON(
    serializedNode: SerializedExtendedTableCellNode,
  ): ExtendedTableCellNode {
    return $applyNodeReplacement(new ExtendedTableCellNode()).updateFromJSON(
      serializedNode,
    );
  }

  constructor(
    headerState?: number,
    colSpan?: number,
    width?: number,
    key?: NodeKey,
  ) {
    super(headerState, colSpan, width, key);
  }

  updateFromJSON(
    serializedNode: LexicalUpdateJSON<SerializedExtendedTableCellNode>,
  ): this {
    const node = super.updateFromJSON(serializedNode);
    node.__borderStyle = serializedNode.borderStyle;
    return node;
  }

  exportJSON(): SerializedExtendedTableCellNode {
    return {
      ...super.exportJSON(),
      ...(this.__borderStyle ? { borderStyle: this.__borderStyle } : {}),
    };
  }

  createDOM(config: EditorConfig): HTMLTableCellElement {
    const dom = super.createDOM(config);
    if (this.__borderStyle) {
      dom.style.cssText += `; ${this.__borderStyle}`;
    }
    return dom;
  }

  // base updateDOM 은 __borderStyle 변화를 감지 못 해 재생성이 안 된다(툴바 테두리 변경이 무반영) → 직접 트리거.
  updateDOM(prevNode: this): boolean {
    return (
      super.updateDOM(prevNode) ||
      prevNode.__borderStyle !== this.__borderStyle
    );
  }

  // 4변 통째 교체(전체 테두리 / 지우기). 한 변만 바꾸려면 setBorderSide.
  setBorderStyle(borderStyle: string | undefined): this {
    const self = this.getWritable();
    self.__borderStyle = borderStyle;
    return self;
  }

  // 나머지 변은 유지한 채 한 변만 갱신(붙여넣은 표의 빠진 한 변만 보정). value 예: '1px solid #000000' | 'none'.
  setBorderSide(side: Side, value: string): this {
    const self = this.getWritable();
    const probe = document.createElement('div');
    probe.style.cssText = self.__borderStyle || '';
    const current = SIDES.map((s) => readSideBorder(probe.style, s));
    current[SIDES.indexOf(side)] = value;
    self.__borderStyle = SIDES.map((s, i) => `border-${s}: ${current[i]}`).join(
      '; ',
    );
    return self;
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const output = super.exportDOM(editor);
    if (this.__borderStyle && isHTMLElement(output.element)) {
      const el = output.element as HTMLElement;
      // 기본 exportDOM 이 박는 border:1px solid black 를 제거하고 원본 border 로 교체
      el.style.removeProperty('border');
      el.style.cssText += `; ${this.__borderStyle}`;
    }
    return output;
  }
}
